/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/core';
import { Input, message, Modal } from 'antd';
import { ModalTitle } from '~components/_common/ModalTitle/ModalTitle';
import { useStyles } from '~src/hooks/useStyles';
import { styleFn } from '~components/molecules/RightSideNav/Modals/ModalFormStyles.style';
import React, { Fragment, useEffect, useRef, useState } from 'react';
import { queryCache, useMutation } from 'react-query';
import { appLog } from '~src/utils/log';
import { Controller, useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { editContract } from '~src/services/api/contracts';

type DbContract = {
  contractId: number;
  data: editContractData;
};

type editContractData = {
  id: number;
  name: string;
  description?: string;
  reference?: string;
};

type EditContractModalProps = {
  projectId: number;
  tenderId: number;
  DbContract?: DbContract;
  onCancel?: () => void;
  onButtonProps?: () => void;
  visible?: boolean;
  data: editContractData | null;
};

export const ContractBuilderEditContractModal: React.FC<EditContractModalProps> = ({
  projectId,
  tenderId,
  onCancel,
  onButtonProps,
  data,
  ...modalProps
}) => {
  const intl = useIntl();
  const styles = useStyles(styleFn);

  const initialFormValues = useForm({
    defaultValues: {
      name: data?.name || '',
      description: data?.description || '',
      reference: data?.reference || '',
    },
  });

  const [formValues, setFormValues] = useState(initialFormValues);

  const [doEditContract, { isLoading }] = useMutation(editContract, {
    onSuccess: () => {
      queryCache.invalidateQueries('contracts');
      message.success('Contract edited successfully');
    },
    onError: (err) => {
      appLog(`creating contract error ->`, err);
    },
  });

  useEffect(() => {
    appLog(`data`, data);
    if (data) {
      initialFormValues.reset({
        name: data.name || '',
        description: data.description || '',
        reference: data?.reference || '',
      });
    }
  }, [data]);

  const isValid = () => {
    const values = initialFormValues.getValues();

    if (!values.name) return false;

    return true;
  };

  const handleSubmit = async () => {
    if (!isValid()) {
      message.error('Please fill in all required fields');
      return;
    }

    const values = initialFormValues.getValues();

    await doEditContract({
      projectId,
      tenderId,
      contractId: data?.id,
      data: {
        name: values.name,
        description: values.description,
        reference: values.reference,
      },
    });

    setFormValues(initialFormValues);
    onCancel?.();
  };

  return (
    <Modal
      title={
        <ModalTitle
          text={intl.formatMessage({ id: 'edit-contract' })}
          icon={['far', 'pen']}
        />
      }
      okText={intl.formatMessage({ id: 'save' })}
      cancelText={intl.formatMessage({ id: 'cancel' })}
      width={425}
      centered
      onOk={handleSubmit}
      confirmLoading={isLoading}
      okButtonProps={{ disabled: !isValid() }}
      onCancel={onCancel}
      destroyOnClose
      {...modalProps}>
      <Fragment>
        <div css={styles.formItem}>
          <p css={styles.formLabel}>
            {intl.formatMessage({ id: 'reference' })}
          </p>
          {/* @ts-ignore */}
          <Controller
            name="reference"
            control={initialFormValues.control}
            render={({ field }) => <Input {...field} />}
          />
        </div>
        <div css={styles.formItem}>
          <p css={styles.formLabel}>{intl.formatMessage({ id: 'name' })}</p>
          {/* @ts-ignore */}
          <Controller
            name="name"
            control={initialFormValues.control}
            render={({ field }) => <Input {...field} />}
          />
        </div>
        <div css={styles.formItem}>
          <p css={styles.formLabel}>
            {intl.formatMessage({ id: 'description' })}
          </p>
          <Controller
            name="description"
            control={initialFormValues.control}
            render={({ field }) => (
              <Input.TextArea
                {...field}
                autoSize={{ minRows: 3, maxRows: 5 }}
              />
            )}
          />
        </div>
      </Fragment>
    </Modal>
  );
};
